import React, { createContext, useContext, useEffect, useState } from 'react';
import { getCurrentUser, signIn, signOut, confirmSignIn, fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';
import { clearWorkspace } from '../utils/workspace';

const AuthContext = createContext(null);

const fetchGroups = async () => {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.payload['cognito:groups'] || [];
};

const syncProfileToLocalStorage = async () => {
  const attrs = await fetchUserAttributes();

  localStorage.setItem('userEmail', attrs.email || '');
  localStorage.setItem('userFirstName', attrs.given_name || '');
  localStorage.setItem('userLastName', attrs.family_name || '');
};

const isTokenExpired = async () => {
  try {
    const session = await fetchAuthSession();
    if (!session.tokens?.accessToken) return true;
    
    const expiresAt = session.tokens.accessToken.payload.exp;
    const currentTime = Math.floor(Date.now() / 1000);
    return expiresAt <= currentTime;
  } catch {
    return true;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  // Check token expiration periodically
  useEffect(() => {
    if (!user) return;

    const checkExpiration = async () => {
      if (await isTokenExpired()) {
        await signOut();
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userFirstName');
        localStorage.removeItem('userLastName');
        setUser(null);
        setGroups([]);
        window.location.href = '/login';
      }
    };

    // Check every 5 minutes
    const interval = setInterval(checkExpiration, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    Promise.all([getCurrentUser(), fetchGroups(), syncProfileToLocalStorage()])
      .then(([currentUser, userGroups]) => {
        setUser(currentUser);
        setGroups(userGroups);
      })
      .catch(() => {
        setUser(null);
        setGroups([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const result = await signIn({ username: email, password, options: { authFlowType: 'USER_PASSWORD_AUTH' } });
    if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      return result;
    }
    const [currentUser, userGroups] = await Promise.all([getCurrentUser(), fetchGroups(), syncProfileToLocalStorage()]);
    setUser(currentUser);
    setGroups(userGroups);
    // A share link visited earlier in this same browser tab leaves
    // sessionStorage's shareToken set indefinitely (nothing else clears it).
    // Left in place, a real login afterwards still gets treated as an
    // anonymous share-link viewer — the settings menu strips Account/Share/
    // Logout down to just Quality. Logging in is a real "no longer a share
    // visitor" event, so clear it here.
    sessionStorage.removeItem('shareToken');
    sessionStorage.removeItem('shareModelId');
    return result;
  };

  const completeNewPassword = async (newPassword) => {
    await confirmSignIn({ challengeResponse: newPassword });
    const [currentUser, userGroups] = await Promise.all([getCurrentUser(), fetchGroups(), syncProfileToLocalStorage()]);
    setUser(currentUser);
    setGroups(userGroups);
    sessionStorage.removeItem('shareToken');
    sessionStorage.removeItem('shareModelId');
  };

  const logout = async () => {
    await signOut();
    clearWorkspace();
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userFirstName');
    localStorage.removeItem('userLastName');
    setUser(null);
    setGroups([]);
  };

  const hasRole = (...roles) => roles.some(role => groups.includes(role));

  // Lets the static header HTML (public/html/*-top.html) show sysAdmin-only
  // menu items (Users) — see styles.css.
  useEffect(() => {
    document.body.classList.toggle('role-sysadmin', groups.includes('sysAdmin'));
  }, [groups]);

  return (
    <AuthContext.Provider value={{ user, groups, loading, login, completeNewPassword, logout, hasRole }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
